import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { planAPI } from '../api/client';
import Breadcrumb from '../components/Breadcrumb';
import { Calendar, Plus, Edit, Trash2, Clock, BookOpen } from 'lucide-react';

const Planning = () => {
  const { orgId } = useParams();
  const { currentOrg, hasAIPermission } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [filter, setFilter] = useState('all'); // all, my-plans

  // Set page title
  useEffect(() => {
    document.title = 'Ders Planları | İlkadım360 Hub';
  }, []);

  useEffect(() => {
    if (orgId || currentOrg) {
      loadPlans();
    }
  }, [orgId, currentOrg, filter]);

  const loadPlans = async () => {
    try {
      const id = orgId || currentOrg?.id;
      if (!id) return;
      
      const response = await planAPI.list(id, filter === 'my-plans');
      setPlans(response.data);
    } catch (error) {
      console.error('Planlar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb 
        items={[
          { label: 'Okullar', path: '/organizations' },
          { label: currentOrg?.name || 'Okul', path: null },
          { label: 'Program & Planlama', path: null }
        ]} 
      />
      
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Program & Planlama</h1>
          <p className="text-gray-600">Ders planlarınızı oluşturun ve yönetin</p>
        </div>
        <div className="flex gap-3">
          {hasAIPermission() && (
            <button
              onClick={() => setShowAIModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 shadow-lg"
            >
              <span className="text-lg">🤖</span>
              AI ile Plan Oluştur
            </button>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={20} />
            Yeni Plan Oluştur
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-4">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              filter === 'all'
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Tüm Planlar
          </button>
          <button
            onClick={() => setFilter('my-plans')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              filter === 'my-plans'
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Planlarım
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      {plans.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <BookOpen className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600 mb-4">Henüz plan oluşturulmamış</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            İlk planı oluşturun →
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div key={plan.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{plan.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{plan.description || 'Açıklama yok'}</p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    plan.status === 'Onaylandı'
                      ? 'bg-green-100 text-green-700'
                      : plan.status === 'Taslak'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  {plan.status}
                </span>
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar size={16} />
                  <span>{new Date(plan.targetDate).toLocaleDateString('tr-TR')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock size={16} />
                  <span>{plan.duration} dakika</span>
                </div>
                {plan.classroom && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Sınıf:</span> {plan.classroom}
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <button 
                  onClick={() => {
                    setSelectedPlan(plan);
                    setShowEditModal(true);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
                >
                  <Edit size={16} />
                  Düzenle
                </button>
                <button 
                  onClick={() => {
                    setSelectedPlan(plan);
                    setShowDeleteModal(true);
                  }}
                  className="flex items-center justify-center px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreatePlanModal
          orgId={orgId || currentOrg?.id}
          initialData={selectedPlan}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedPlan(null);
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            setSelectedPlan(null);
            loadPlans();
          }}
        />
      )}

      {/* AI Modal */}
      {showAIModal && (
        <AIPlanModal
          orgId={orgId || currentOrg?.id}
          onClose={() => setShowAIModal(false)}
          onSuccess={(aiPlan) => {
            setShowAIModal(false);
            // Open create modal with AI-generated content
            setSelectedPlan(aiPlan);
            setShowCreateModal(true);
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedPlan && (
        <EditPlanModal
          orgId={orgId || currentOrg?.id}
          plan={selectedPlan}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPlan(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedPlan(null);
            loadPlans();
          }}
        />
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedPlan && (
        <DeletePlanModal
          orgId={orgId || currentOrg?.id}
          plan={selectedPlan}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedPlan(null);
          }}
          onSuccess={() => {
            setShowDeleteModal(false);
            setSelectedPlan(null);
            loadPlans();
          }}
        />
      )}
    </div>
  );
};

const CreatePlanModal = ({ orgId, initialData, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    description: initialData?.description || '',
    objectives: initialData?.objectives || '',
    targetDate: initialData?.targetDate || '',
    duration: initialData?.duration || 45,
    classroom: initialData?.classroom || '',
    materials: initialData?.materials || '',
    activities: initialData?.activities || '',
    notes: initialData?.notes || '',
    status: 'Taslak',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await planAPI.create(orgId, {
        ...formData,
        targetDate: new Date(formData.targetDate).toISOString(),
      });
      onSuccess();
    } catch (error) {
      console.error('Plan oluşturulamadı:', error);
      alert('Plan oluşturulamadı: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Yeni Ders Planı Oluştur</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Plan Başlığı *</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Örn: Sayılarla Tanışma"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Açıklama</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Kısa açıklama"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Hedef Tarih *</label>
              <input
                type="date"
                required
                value={formData.targetDate}
                onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Süre (dakika) *</label>
              <input
                type="number"
                required
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sınıf/Grup Adı</label>
            <input
              type="text"
              value={formData.classroom}
              onChange={(e) => setFormData({ ...formData, classroom: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Örn: Kelebekler Sınıfı"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Kazanımlar</label>
            <textarea
              value={formData.objectives}
              onChange={(e) => setFormData({ ...formData, objectives: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Bu derste çocuklar..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Etkinlikler</label>
            <textarea
              value={formData.activities}
              onChange={(e) => setFormData({ ...formData, activities: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="1. Açılış çemberi...\n2. Ana etkinlik...\n3. Kapanış..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Materyaller</label>
            <textarea
              value={formData.materials}
              onChange={(e) => setFormData({ ...formData, materials: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="- Renkli bloklar\n- Sayı kartları\n- Tahta"
            />
          </div>

          <div className="flex gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Oluşturuluyor...' : 'Planı Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditPlanModal = ({ orgId, plan, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: plan.title || '',
    description: plan.description || '',
    objectives: plan.objectives || '',
    targetDate: plan.targetDate ? new Date(plan.targetDate).toISOString().split('T')[0] : '',
    duration: plan.duration || 45,
    classroom: plan.classroom || '',
    materials: plan.materials || '',
    activities: plan.activities || '',
    notes: plan.notes || '',
    status: plan.status || 'Taslak',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await planAPI.update(orgId, plan.id, {
        ...formData,
        targetDate: new Date(formData.targetDate).toISOString(),
      });
      onSuccess();
    } catch (error) {
      console.error('Plan güncellenemedi:', error);
      alert('Plan güncellenemedi: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Ders Planını Düzenle</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Plan Başlığı *</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Örn: Sayılarla Tanışma"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Açıklama</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Kısa açıklama"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Hedef Tarih *</label>
              <input
                type="date"
                required
                value={formData.targetDate}
                onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Süre (dakika) *</label>
              <input
                type="number"
                required
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Durum</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="Taslak">Taslak</option>
              <option value="Onaylandı">Onaylandı</option>
              <option value="Tamamlandı">Tamamlandı</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sınıf/Grup Adı</label>
            <input
              type="text"
              value={formData.classroom}
              onChange={(e) => setFormData({ ...formData, classroom: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Örn: Kelebekler Sınıfı"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Kazanımlar</label>
            <textarea
              value={formData.objectives}
              onChange={(e) => setFormData({ ...formData, objectives: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Bu derste çocuklar..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Etkinlikler</label>
            <textarea
              value={formData.activities}
              onChange={(e) => setFormData({ ...formData, activities: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="1. Açılış çemberi...\n2. Ana etkinlik...\n3. Kapanış..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Materyaller</label>
            <textarea
              value={formData.materials}
              onChange={(e) => setFormData({ ...formData, materials: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="- Renkli bloklar\n- Sayı kartları\n- Tahta"
            />
          </div>

          <div className="flex gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Güncelleniyor...' : 'Değişiklikleri Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeletePlanModal = ({ orgId, plan, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);

    try {
      await planAPI.delete(orgId, plan.id);
      onSuccess();
    } catch (error) {
      console.error('Plan silinemedi:', error);
      alert('Plan silinemedi: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-red-100 rounded-full">
            <Trash2 className="text-red-600" size={24} />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Planı Sil</h2>
        </div>

        <p className="text-gray-600 mb-2">
          <strong>{plan.title}</strong> planını silmek istediğinizden emin misiniz?
        </p>
        <p className="text-sm text-gray-500 mb-6">
          Bu işlem geri alınamaz.
        </p>

        <div className="flex gap-4">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            İptal
          </button>
          <button
            onClick={handleDelete}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? 'Siliniyor...' : 'Evet, Sil'}
          </button>
        </div>
      </div>
    </div>
  );
};



const AIPlanModal = ({ orgId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    ageGroup: '3-4 yaş',
    duration: 45,
    classroom: '',
    focusArea: '',
  });
  const [loading, setLoading] = useState(false);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await planAPI.aiGenerate(orgId, formData);
      const aiPlan = response.data;
      
      // Pass AI-generated content to parent
      onSuccess(aiPlan);
    } catch (error) {
      console.error('AI plan oluşturulamadı:', error);
      
      // Extract meaningful error message
      let errorMessage = 'Bilinmeyen bir hata oluştu';
      
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail;
        
        // Handle different status codes
        if (status === 502 || status === 503 || status === 504) {
          errorMessage = 'AI servisinde geçici bir sorun var. Lütfen birkaç dakika sonra tekrar deneyin.';
        } else if (status === 500) {
          if (typeof detail === 'string') {
            errorMessage = detail.includes('timeout') 
              ? 'AI model yanıt vermedi. Lütfen farklı bir model seçin veya daha kısa bir plan deneyin.'
              : detail;
          } else if (detail && typeof detail === 'object') {
            errorMessage = detail.message || JSON.stringify(detail);
          }
        } else if (status === 403) {
          errorMessage = 'Bu işlem için yetkiniz bulunmuyor.';
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail && typeof detail === 'object') {
          errorMessage = detail.message || 'Sunucu hatası';
        }
      } else if (error.message) {
        if (error.message.includes('Network Error') || error.message.includes('fetch')) {
          errorMessage = 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
        } else {
          errorMessage = error.message;
        }
      }
      
      alert('AI plan oluşturulamadı: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg">
            <span className="text-3xl">🤖</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">AI ile Plan Oluştur</h2>
            <p className="text-sm text-gray-600">Yapay zeka destekli ders planı üretimi</p>
          </div>
        </div>

        <form onSubmit={handleGenerate} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Plan Başlığı / Konusu *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              placeholder="Örn: Renklerle Tanışma, Sayılar ve Şekiller"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Yaş Grubu *
              </label>
              <select
                required
                value={formData.ageGroup}
                onChange={(e) => setFormData({ ...formData, ageGroup: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="0-1 yaş">0-1 yaş (Bebekler)</option>
                <option value="1-2 yaş">1-2 yaş (Yeni Yürüyenler)</option>
                <option value="2-3 yaş">2-3 yaş</option>
                <option value="3-4 yaş">3-4 yaş</option>
                <option value="4-5 yaş">4-5 yaş</option>
                <option value="5-6 yaş">5-6 yaş</option>
                <option value="Karma">Karma Yaş Grubu</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Süre (dakika) *
              </label>
              <input
                type="number"
                required
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Odak Alan (Opsiyonel)
            </label>
            <select
              value={formData.focusArea}
              onChange={(e) => setFormData({ ...formData, focusArea: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Genel Gelişim</option>
              <option value="Matematik">Matematik</option>
              <option value="Dil Gelişimi">Dil Gelişimi</option>
              <option value="Sanat">Sanat ve Yaratıcılık</option>
              <option value="Müzik">Müzik</option>
              <option value="Hareket">Hareket ve Oyun</option>
              <option value="Fen">Fen ve Doğa</option>
              <option value="Sosyal">Sosyal ve Duygusal Gelişim</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sınıf/Grup Adı (Opsiyonel)
            </label>
            <input
              type="text"
              value={formData.classroom}
              onChange={(e) => setFormData({ ...formData, classroom: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              placeholder="Örn: Kelebekler Sınıfı"
            />
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>💡 İpucu:</strong> AI, seçtiğiniz yaş grubuna uygun kazanımlar, etkinlikler ve materyaller önerecektir.
              Oluşturulan planı istediğiniz gibi düzenleyebilirsiniz.
            </p>
          </div>

          <div className="flex gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  AI Üretiyor...
                </>
              ) : (
                <>
                  <span>🤖</span>
                  Plan Oluştur
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Planning;
